import { useTranslation } from 'react-i18next';
import { Modal, Form, FormField, Button, useZodForm } from '@noor/ui';
import { toast, useFormSeed, type ModalRenderProps } from '@noor/lib';
import { getApiErrorMessage, getApiFieldErrors } from '@noor/api';
import {
  ASSIGNABLE_ROLES,
  formatUserPhone,
  roleLabelKey,
  useUser,
  useUserLocations,
  useEmployeeLocations,
} from '@/entities/user';
import {
  buildCreateUserFormSchema,
  buildEditUserFormSchema,
  type CreateUserFormValues,
  type EditUserFormValues,
} from '../model/schema';
import {
  useCreateUser,
  useRenameUser,
  useUpdateUserLocations,
} from '../model/use-upsert-user';
import { toCreateUserFieldErrors } from '../lib/attach-errors';

const MODAL =
  'w-[676px] max-w-[calc(100vw-2rem)] gap-0 rounded-[24px] border-0 bg-[var(--lg-surface)] p-8 text-[var(--lg-fg)] shadow-lg [--lg-fg:#000] [--lg-grey-100:#f3f4f6] [--lg-grey-200:#e5e7eb] [--lg-grey-400:#9ca3af] [--lg-orange-500:#ff6d00] [--lg-orange-600:#ea580c] [--lg-surface:#fff] sm:max-w-[676px] [&_[data-slot=dialog-close]]:top-8 [&_[data-slot=dialog-close]]:right-8 [&_[data-slot=dialog-close]]:size-6 [&_[data-slot=dialog-close]]:rounded-none [&_[data-slot=dialog-close]]:hover:bg-transparent [&_[data-slot=dialog-header]]:mb-[52px] [&_[data-slot=dialog-header]]:pr-10';

/** White bordered field pill from the Figma employee modal. */
const FIELD =
  'h-12 rounded-[24px] border-[var(--lg-grey-200)] bg-[var(--lg-surface)] px-4 py-2.5 text-sm font-medium tracking-[-0.28px] text-[var(--lg-fg)] shadow-[0_1px_2px_0_rgb(10_13_20_/_3%)] placeholder:text-[var(--lg-grey-400)] disabled:cursor-not-allowed disabled:opacity-60';

const READONLY_FIELD =
  'h-12 rounded-[24px] border border-[var(--lg-grey-200)] bg-[var(--lg-grey-100)] px-4 py-2.5 text-sm font-medium tracking-[-0.28px] text-[var(--lg-fg)] shadow-[0_1px_2px_0_rgb(10_13_20_/_3%)] outline-none';

const FORM_GRID = 'grid grid-cols-2 gap-x-3 gap-y-6';

/**
 * Validation text, taken OUT OF FLOW into the space {@link GROUP} reserves.
 *
 * A `FormField` is a grid, so an in-flow message is a grid ROW: an errored
 * field grew taller and shifted every field below it down the form. Absolute
 * positioning means an error can never change a field's height.
 */
const MESSAGE = 'pointer-events-none absolute top-full left-0 min-h-0 px-4 empty:hidden';

/**
 * Field wrapper: anchors {@link MESSAGE} and reserves its line on every field,
 * errored or not, so validation never moves the form.
 */
const GROUP = 'relative gap-1.5';

const LABEL =
  'px-1 text-sm font-medium leading-none tracking-[-0.28px] text-[var(--lg-fg)]';

type UserFormModalProps = ModalRenderProps<'add-user'> | ModalRenderProps<'edit-user'>;

/**
 * Create / edit employee modal, registered under `add-user` / `edit-user`.
 *
 * The two modes are genuinely different forms, not one form with a disabled
 * field, because the API's create and update surfaces barely overlap:
 *
 * | Mode   | Endpoint                   | Collects                    |
 * | ------ | -------------------------- | --------------------------- |
 * | create | `POST merchant-employees`  | names, phone, role          |
 * | edit   | `PATCH .../{id}`           | names ONLY                  |
 *
 * The edit body accepts `first_name`/`last_name` and nothing else — phone and
 * role belong to auth-service — so the edit form renders those two as static
 * context rather than inputs whose edits the request would silently drop.
 *
 * There is no password step, and no success alert revealing one: the identity
 * (and its credentials) is minted by auth-service during the attach, and no
 * response on this service returns a password.
 */
export function UserFormModalDesktop({ payload, close }: UserFormModalProps) {
  return payload.mode === 'edit' ? (
    <EditUserForm userId={payload.user.id} close={close} />
  ) : (
    <CreateUserForm
      close={close}
      initialRole={payload.initialRole}
      onCreated={payload.onCreated}
    />
  );
}

/* ------------------------------- Create ------------------------------- */

function CreateUserForm({
  close,
  initialRole,
  onCreated,
}: {
  close: () => void;
  initialRole?: CreateUserFormValues['role'] | undefined;
  onCreated?: ((role: CreateUserFormValues['role']) => void) | undefined;
}) {
  const { t } = useTranslation('eats');
  const create = useCreateUser();

  const schema = buildCreateUserFormSchema(t);
  const form = useZodForm(schema, {
    defaultValues: {
      firstName: '',
      lastName: '',
      phone: '',
      role: initialRole ?? 'manager',
    },
    mode: 'onSubmit',
  });

  const roleOptions = ASSIGNABLE_ROLES.map((role) => ({
    label: t(roleLabelKey(role)),
    value: role,
  }));

  function handleSubmit(values: CreateUserFormValues): void {
    create.mutate(values, {
      onSuccess: () => {
        toast.success(t('users.form.created'));
        close();
        onCreated?.(values.role);
      },
      // Surface the server's OWN reason. An attach is rejected for specific,
      // actionable causes — a phone auth-service refuses, a role the merchant
      // contour may not assign, an identity already bound elsewhere — and the
      // generic "something went wrong" that stood here hid every one of them,
      // which is what made a failing attach impossible to act on.
      onError: (error) => {
        const matched = toCreateUserFieldErrors(getApiFieldErrors(error));
        for (const [field, message] of matched) form.setError(field, { message });
        // Anything not attributable to a field goes to the form-level slot, so
        // a rejection is never silent.
        if (matched.length === 0) {
          form.setError('root', {
            message: getApiErrorMessage(error, t('users.form.errorDescription')),
          });
        }
      },
    });
  }

  return (
    <Modal title={t('common:modal.creation')} className={MODAL}>
      <Form form={form} onSubmit={handleSubmit} className="flex flex-col">
        <div className={FORM_GRID}>
          <FormField name="lastName" className={GROUP}>
            <FormField.Label className={LABEL}>
              {t('users.form.lastName')}
            </FormField.Label>
            <FormField.Input className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>

          <FormField name="firstName" className={GROUP}>
            <FormField.Label className={LABEL}>
              {t('users.form.firstName')}
            </FormField.Label>
            <FormField.Input className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>

          <FormField name="phone" className={GROUP}>
            <FormField.Label className={LABEL}>{t('users.form.phone')}</FormField.Label>
            <FormField.Phone className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>

          <FormField name="role" className={GROUP}>
            <FormField.Label className={LABEL}>{t('users.form.role')}</FormField.Label>
            <FormField.Select options={roleOptions} className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>
        </div>

        <Form.RootError className="mt-1 px-1" />

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            disabled={create.isPending}
            className="h-11 min-w-[132px] rounded-full bg-[var(--lg-orange-500)] px-6 text-sm font-semibold tracking-[-0.28px] text-[var(--lg-surface)] shadow-none hover:bg-[var(--lg-orange-600)] hover:shadow-none"
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

/* -------------------------------- Edit -------------------------------- */

function EditUserForm({ userId, close }: { userId: string; close: () => void }) {
  const { t } = useTranslation('eats');
  const rename = useRenameUser();
  const updateLocations = useUpdateUserLocations();
  const { data: locations = [] } = useUserLocations();
  const { data: selectedLocationIds = [] } = useEmployeeLocations(userId);

  // Re-read by id on every open, seeded on `dataUpdatedAt` — CLAUDE.md §8's
  // "Edit forms" rule, so reopening never replays a pre-edit copy from cache.
  const { data, dataUpdatedAt } = useUser(userId);

  const schema = buildEditUserFormSchema(t);
  const form = useZodForm(schema, {
    defaultValues: { firstName: '', lastName: '', locationIds: [] },
    mode: 'onSubmit',
  });

  const { reset } = form;
  useFormSeed(true, `${userId}:${selectedLocationIds.join(',')}`, dataUpdatedAt, () => {
    reset({
      firstName: data?.firstName ?? '',
      lastName: data?.lastName ?? '',
      locationIds: selectedLocationIds.map(String),
    });
  });

  function handleSubmit(values: EditUserFormValues): void {
    Promise.all([
      rename.mutateAsync({
        id: userId,
        firstName: values.firstName,
        lastName: values.lastName,
      }),
      updateLocations.mutateAsync({
        id: userId,
        locationIds: values.locationIds.map(Number),
      }),
    ])
      .then(() => {
        toast.success(t('users.form.updated'));
        close();
      })
      .catch((error) =>
        form.setError('root', {
          message: getApiErrorMessage(error, t('users.form.errorDescription')),
        }),
      );
  }

  return (
    <Modal title={t('common:modal.edition')} className={MODAL}>
      <Form form={form} onSubmit={handleSubmit} className="flex flex-col">
        <div className={FORM_GRID}>
          <FormField name="lastName" className={GROUP}>
            <FormField.Label className={LABEL}>
              {t('users.form.lastName')}
            </FormField.Label>
            <FormField.Input className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>

          <FormField name="firstName" className={GROUP}>
            <FormField.Label className={LABEL}>
              {t('users.form.firstName')}
            </FormField.Label>
            <FormField.Input className={FIELD} />
            <FormField.Message className={MESSAGE} />
          </FormField>

          {/* Phone and role are NOT editable here — the PATCH body carries only
           * the two names. Disabled inputs match the edit-form design while
           * making the API limit explicit in the control state. */}
          <DisabledField
            label={t('users.form.phone')}
            value={data?.phone ? formatUserPhone(data.phone) : '—'}
          />
          <DisabledField
            label={t('users.form.role')}
            value={data && data.role !== 'unknown' ? t(roleLabelKey(data.role)) : '—'}
          />
          <FormField name="locationIds" className={`${GROUP} col-span-2`}>
            <FormField.Label className={LABEL}>Филиалы</FormField.Label>
            <FormField.MultiSelect
              options={locations.map((location) => ({
                value: String(location.id),
                label: location.name,
              }))}
              placeholder="Выберите филиалы"
              searchPlaceholder="Поиск филиала"
            />
            <FormField.Message className={MESSAGE} />
          </FormField>
        </div>

        <Form.RootError className="mt-1 px-1" />

        <div className="mt-8 flex justify-end">
          <Button
            type="submit"
            disabled={rename.isPending}
            className="h-11 min-w-[132px] rounded-full bg-[var(--lg-orange-500)] px-6 text-sm font-semibold tracking-[-0.28px] text-[var(--lg-surface)] shadow-none hover:bg-[var(--lg-orange-600)] hover:shadow-none"
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

function DisabledField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1.5 pb-3">
      <span className={LABEL}>{label}</span>
      <input className={READONLY_FIELD} value={value} readOnly aria-readonly="true" />
    </div>
  );
}
